import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import SearchableSelect from '../../components/common/SearchableSelect';
import CollapsibleFilterPanel, { FilterField, filterInputProps } from '../../components/common/CollapsibleFilterPanel';
import { orderAPI, brandAPI, productAPI, adminAPI, sheetAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import RefundDeadlineBadge from '../../components/common/RefundDeadlineBadge';
import { isRefundPending } from '../../utils/refundTimeline';
import { tableStyles, actionBtn, statusBadge } from '../../styles/orderTableStyles';
import { todayIsoDate } from '../../utils/dateTime';
import PaginationBar from '../../components/common/PaginationBar';

const PaginationComponent = ({ pagination, onPageChange }) => (
  <PaginationBar
    page={pagination.currentPage}
    totalPages={pagination.totalPages}
    totalElements={pagination.totalItems}
    pageSize={pagination.pageSize}
    pageBase={1}
    onPageChange={onPageChange}
    style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#ffffff' }}
  />
);


const formatPaymentMethodLabel = (method) => {
  if (!method) return '—';
  if (method === 'UPI') return 'UPI';
  if (method === 'ACCOUNT') return 'Bank Account';
  return method;
};

const buyerHasPaymentDetails = (order) =>
  Boolean(
    order?.paymentMethod ||
    order?.upiId ||
    order?.bankAccount ||
    order?.bankName ||
    order?.ifscCode ||
    order?.accountHolderName
  );

const MediatorOrders = () => {
  const { mediatorId } = useAuth();
  const [urlSearchParams] = useSearchParams();
  const statusQuery = urlSearchParams.get('status') || '';
  const refundAlertQuery = (urlSearchParams.get('refundAlert') || '').toUpperCase();
  const urlStatusFilter = useMemo(() => {
    if (!statusQuery) return [];
    return statusQuery.split(',').map((s) => s.trim()).filter(Boolean);
  }, [statusQuery]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const [showBulkStatusDropdown, setShowBulkStatusDropdown] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState(null);
  const [showRefundOnly, setShowRefundOnly] = useState(false);
  const [downloadingSheet, setDownloadingSheet] = useState(false);
  
  // Dropdown data
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [orderStatuses] = useState([
    { value: 'ORDER_FORM_FILLED', label: 'Order Form Filled' },
    { value: 'REFUND_FORM_FILLED', label: 'Refund Form Filled' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'PAID', label: 'Paid' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'REJECT_AND_REFILL', label: 'Reject and Refill' },
    { value: 'ORDER_FORM_REFILL_REQUEST', label: 'Order Form Refill Request' },
    { value: 'REFUND_FORM_REFILL_REQUEST', label: 'Refund Form Refill Request' },
    { value: 'PAID_TO_MEDIATOR', label: 'Paid to Mediator' },
    { value: 'PAID_TO_BUYER', label: 'Paid to Buyer' }
  ]);
  
  // Search form state
  const [searchForm, setSearchForm] = useState({
    page: 0,
    size: 10,
    sortBy: 'creationDate',
    sortDirection: 'DESC',
    orderId: '',
    status: '',
    userEmail: '',
    productId: '',
    brandId: '',
    refundAlert: '',
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  });
  const orderStatusOptions = orderStatuses.map((status) => ({ value: status.value, label: status.label }));
  const brandOptions = brands.map((brand) => ({ value: brand.id, label: brand.brandName }));
  const productOptions = products.map((product) => ({ value: product.id, label: product.productName || product.productCode || `Product ${product.id}` }));

  const activeFilterCount = useMemo(
    () => ['orderId', 'status', 'userEmail', 'productId', 'brandId', 'refundAlert'].filter((k) => searchForm[k]).length,
    [searchForm]
  );

  const fetchOrders = async (page = 1, searchParamsOverride = null, medId = mediatorId) => {
    const effectiveMedId = medId || mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id;
    if (!effectiveMedId) {
      console.error('fetchOrders: No mediatorId found in arguments, state, or localStorage');
      return;
    }

    setLoading(true);
    try {
      const source = searchParamsOverride || searchForm;
      const params = {
        ...source,
        page: page - 1,
        size: source.size || 10,
        sortBy: source.sortBy || 'creationDate',
        sortDirection: source.sortDirection || 'DESC',
        mediatorId: effectiveMedId,
      };
      if (params.status && !Array.isArray(params.status)) {
        params.status = [params.status];
      }
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
          delete params[key];
        }
      });

      const response = await orderAPI.searchOrders(params);
      const data = response.data;
      const mappedOrders = (data.data || []).map(order => ({
        id: order.id,
        orderId: order.orderId,
        customer: order.reviewerName || order.userEmail,
        product: order.productName || `Product ${order.productId}`,
        amount: order.orderAmount,
        status: order.orderStatus,
        date: order.orderDate,
        userEmail: order.userEmail,
        mediatorId: order.mediatorId,
        mediatorName: order.mediatorName,
        productId: order.productId,
        productName: order.productName,
        brandId: order.brandId,
        brandName: order.brandName,
        lessAmount: order.lessAmount,
        dealFeedbackType: order.dealFeedbackType,
        dealOrderType: order.dealOrderType,
        whatsappNumber: order.whatsappNumber,
        reason: order.reason,
        paymentMethod: order.paymentMethod,
        accountHolderName: order.accountHolderName,
        bankAccount: order.bankAccount,
        bankName: order.bankName,
        ifscCode: order.ifscCode,
        upiId: order.upiId,
        orderScreenshot: order.orderScreenshot,
        deliveredOrderScreenshot: order.deliveredOrderScreenshot,
        returnWindowCloseScreenshot: order.returnWindowCloseScreenshot,
        reviewRatingScreenshot: order.reviewRatingScreenshot,
        sellerFeedbackScreenshot: order.sellerFeedbackScreenshot,
        refundTimeline: order.refundTimeline,
        refundFormFilledDate: order.refundFormFilledDate,
        penaltyAmount: order.penaltyAmount,
        orderStatus: order.orderStatus,
      }));
      setOrders(mappedOrders);
      setPagination({
        currentPage: page,
        totalPages: data.totalPages || 1,
        totalItems: data.totalElements || 0,
        pageSize: data.size || 10
      });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [productsRes, brandsRes] = await Promise.all([
          productAPI.getActiveProducts(),
          brandAPI.getActiveBrands()
        ]);
        setProducts(productsRes.data || []);
        setBrands(brandsRes.data.data || []);
      } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const effectiveMedId = mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id;
    if (!effectiveMedId) return;

    const params = {
      page: 0,
      size: 10,
      sortBy: 'creationDate',
      sortDirection: 'DESC',
      orderId: '',
      status: urlStatusFilter.length ? urlStatusFilter[0] : '',
      userEmail: '',
      productId: '',
      brandId: '',
      refundAlert: refundAlertQuery || '',
      mediatorId: effectiveMedId,
    };
    setSearchForm(params);
    fetchOrders(1, {
      ...params,
      status: urlStatusFilter.length ? urlStatusFilter : undefined,
    }, effectiveMedId);
  }, [mediatorId, statusQuery, refundAlertQuery]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setSearchLoading(true);
    try {
      await fetchOrders(1, searchForm);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleReset = () => {
    const cleared = {
      page: 0, size: 10, sortBy: 'creationDate', sortDirection: 'DESC',
      orderId: '', status: '', userEmail: '', productId: '', brandId: '', refundAlert: '',
    };
    setSearchForm(cleared);
    fetchOrders(1, cleared);
  };

  const handleInputChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    if (searchForm.orderId) {
      chips.push({ key: 'orderId', label: 'Order ID', value: searchForm.orderId, onClear: () => handleInputChange('orderId', '') });
    }
    if (searchForm.status) {
      const label = orderStatusOptions.find((o) => o.value === searchForm.status)?.label || searchForm.status;
      chips.push({ key: 'status', label: 'Status', value: label, onClear: () => handleInputChange('status', '') });
    }
    if (searchForm.refundAlert) {
      const labels = {
        CLOSING_SOON: 'Closing soon',
        URGENT: 'Urgent / penalty risk',
        OVERDUE: 'Overdue reminders',
      };
      chips.push({
        key: 'refundAlert',
        label: 'Refund alert',
        value: labels[searchForm.refundAlert] || searchForm.refundAlert,
        onClear: () => handleInputChange('refundAlert', ''),
      });
    }
    if (searchForm.userEmail) {
      chips.push({ key: 'userEmail', label: 'Email', value: searchForm.userEmail, onClear: () => handleInputChange('userEmail', '') });
    }
    if (searchForm.brandId) {
      const label = brandOptions.find((o) => o.value === searchForm.brandId)?.label || searchForm.brandId;
      chips.push({ key: 'brandId', label: 'Brand', value: label, onClear: () => handleInputChange('brandId', '') });
    }
    if (searchForm.productId) {
      const label = productOptions.find((o) => o.value === searchForm.productId)?.label || searchForm.productId;
      chips.push({ key: 'productId', label: 'Product', value: label, onClear: () => handleInputChange('productId', '') });
    }
    return chips;
  }, [searchForm, orderStatusOptions, brandOptions, productOptions]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    setActiveDropdown(null);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  const handleDropdownToggle = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);

  const handleUpdateStatus = (orderId, targetStatus) => {
    const statusLabel = getStatusBadge(targetStatus).label;
    
    // Check if reason is required for this status
    if (['REJECTED', 'REJECT_AND_REFILL', 'ORDER_FORM_REFILL_REQUEST', 'REFUND_FORM_REFILL_REQUEST'].includes(targetStatus)) {
      setPendingStatusUpdate({ orderId, targetStatus });
      setReason('');
      setShowReasonModal(true);
      setActiveDropdown(null);
    } else {
      setSelectedOrder(orders.find(o => o.id === orderId));
      setStatusToUpdate({ orderId, currentStatus: targetStatus });
      setShowStatusConfirm(true);
    }
  };

  const confirmReasonUpdate = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required for this status');
      return;
    }

    setUpdatingStatus(pendingStatusUpdate.orderId);
    try {
      await adminAPI.updateOrderStatus(pendingStatusUpdate.orderId, pendingStatusUpdate.targetStatus, reason);
      toast.success(`Order status updated to ${getStatusBadge(pendingStatusUpdate.targetStatus).label}`);
      fetchOrders(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
      setShowReasonModal(false);
      setPendingStatusUpdate(null);
      setReason('');
    }
  };

  const confirmStatusUpdate = async (newStatus) => {
    setUpdatingStatus(statusToUpdate.orderId);
    try {
      await adminAPI.updateOrderStatus(statusToUpdate.orderId, newStatus, '');
      toast.success(`Order status updated to ${getStatusBadge(newStatus).label}`);
      fetchOrders(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
      setShowStatusConfirm(false);
      setStatusToUpdate(null);
    }
  };

  const handleSelectOrder = (id) => {
    setSelectedOrderIds(prev => 
      prev.includes(id) ? prev.filter(orderId => orderId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map(o => o.id));
    }
  };

  const handleBulkMarkClick = (status) => {
    setBulkTargetStatus(status);
    setShowBulkConfirm(true);
    setShowBulkStatusDropdown(false);
  };

  const confirmBulkMark = async () => {
    if (selectedOrderIds.length === 0 || !bulkTargetStatus) return;
    
    setIsBulkMarking(true);
    try {
      let successCount = 0;
      for (const id of selectedOrderIds) {
        try {
          await adminAPI.updateOrderStatus(id, bulkTargetStatus);
          successCount++;
        } catch (err) {
          console.error(`Failed to update order ${id}`, err);
        }
      }
      
      toast.success(`Successfully marked ${successCount} orders as ${getStatusBadge(bulkTargetStatus).label}`);
      setSelectedOrderIds([]);
      fetchOrders(pagination.currentPage);
    } catch (error) {
      toast.error('Failed to complete bulk update');
    } finally {
      setIsBulkMarking(false);
      setShowBulkConfirm(false);
      setBulkTargetStatus(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      ORDER_FORM_FILLED: { color: '#3b82f6', bg: '#dbeafe', label: 'Order Filled' },
      REFUND_FORM_FILLED: { color: '#f59e0b', bg: '#fef3c7', label: 'Refund Filled' },
      APPROVED: { color: '#10b981', bg: '#d1fae5', label: 'Approved' },
      PAID: { color: '#059669', bg: '#ecfdf5', label: 'Paid' },
      REJECTED: { color: '#ef4444', bg: '#fee2e2', label: 'Rejected' },
      REJECT_AND_REFILL: { color: '#be185d', bg: '#fce7f3', label: 'Reject and Refill' },
      ORDER_FORM_REFILL_REQUEST: { color: '#ec4899', bg: '#fdf2f8', label: 'Order Refill' },
      REFUND_FORM_REFILL_REQUEST: { color: '#ec4899', bg: '#fdf2f8', label: 'Refund Refill' },
      PAID_TO_MEDIATOR: { color: '#059669', bg: '#ecfdf5', label: 'Paid to Mediator' },
      PAID_TO_BUYER: { color: '#2563eb', bg: '#eff6ff', label: 'Paid to Buyer' }
    };
    return config[status] || { color: '#6b7280', bg: '#f3f4f6', label: status };
  };

  const handleWhatsAppClick = (number) => {
    if (!number) {
      toast.error('WhatsApp number not available');
      return;
    }
    const cleanNumber = number.replace(/\D/g, '');
    const url = `https://wa.me/91${cleanNumber.length === 10 ? cleanNumber : cleanNumber}`;
    window.open(url, '_blank');
  };

  const handleDownloadSheet = async () => {
    const effectiveMedId = mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id;
    if (!effectiveMedId) return;
    setDownloadingSheet(true);
    try {
      const response = await sheetAPI.generateSheet({
        fileName: `Mediator_Orders_${todayIsoDate()}`,
        mediatorId: effectiveMedId,
        sheetFor: 'MEDIATOR',
      });
      const payload = response.data?.data || response.data;
      const url = payload?.sheetUrl || payload?.fileUrl;
      if (url) window.open(url, '_blank');
      toast.success('Order sheet ready');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate sheet');
    } finally {
      setDownloadingSheet(false);
    }
  };

  const displayedOrders = showRefundOnly
    ? orders.filter((order) => isRefundPending(order.status))
    : orders;

  const urgentCount = orders.filter((o) =>
    isRefundPending(o.status) &&
    ['REMINDER', 'PENALTY_50', 'PENALTY_100', 'CLOSED'].includes(o.refundTimeline?.warningLevel)
  ).length;

  const formsFilledCount = orders.filter((o) =>
    ['ORDER_FORM_FILLED', 'REFUND_FORM_FILLED', 'APPROVED', 'PAID', 'PAID_TO_MEDIATOR', 'PAID_TO_BUYER'].includes(o.status)
  ).length;

  const pendingRefundCount = orders.filter((o) => isRefundPending(o.status)).length;

  return (
    <div className="orders-page-shell" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div className="orders-page-top page-section-top" style={{ padding: '20px 24px 12px 24px', backgroundColor: '#f8fafc', flexShrink: 0, overflow: 'visible' }}>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Forms Filled</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginTop: '4px' }}>{formsFilledCount}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Pending Refunds</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#d97706', marginTop: '4px' }}>{pendingRefundCount}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600 }}>Need Notification</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#92400e', marginTop: '4px' }}>{urgentCount}</div>
          </div>
        </div>
        {urgentCount > 0 && (
          <div style={{
            marginBottom: '16px', padding: '16px 20px', borderRadius: '14px',
            background: '#fffbeb', border: '1px solid #fde68a',
            fontSize: '16px', fontWeight: 700, color: '#92400e'
          }}>
            {urgentCount} order(s) need refund follow-up. Use WhatsApp to contact buyers before penalties apply.
          </div>
        )}
        <div className="page-toolbar admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>My Orders</h2>
          <div className="page-toolbar-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant={showRefundOnly ? 'primary' : 'secondary'}
              onClick={() => setShowRefundOnly(!showRefundOnly)}
              style={{ fontSize: '15px', padding: '10px 16px' }}
            >
              {showRefundOnly ? 'Show All' : 'Needs Refund Only'}
            </Button>
            <Button variant="secondary" onClick={handleDownloadSheet} loading={downloadingSheet} style={{ fontSize: '15px', padding: '10px 16px' }}>
              <Icon name="file" size={16} /> Download Sheet
            </Button>
            {selectedOrderIds.length > 0 && (
              <div style={{ position: 'relative' }}>
                <Button 
                  variant="primary" 
                  onClick={() => setShowBulkStatusDropdown(!showBulkStatusDropdown)} 
                  loading={isBulkMarking}
                  style={{ background: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  Mark Selected ({selectedOrderIds.length}) <Icon name="chev" size={16} />
                </Button>
                {showBulkStatusDropdown && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', zIndex: 100, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '8px', minWidth: '200px', marginTop: '4px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', padding: '4px 12px' }}>Set Status To:</div>
                    {[
                      { value: 'PAID_TO_MEDIATOR', label: 'Received Payment' },
                      { value: 'PAID_TO_BUYER', label: 'Paid to Buyer' }
                    ].map(status => {
                      // Restriction for bulk: Can only change to PAID_TO_BUYER if ALL selected orders are PAID_TO_MEDIATOR or PAID_TO_BUYER
                      let isBulkDisabled = false;
                      if (status.value === 'PAID_TO_BUYER') {
                        isBulkDisabled = selectedOrderIds.some(id => {
                          const order = orders.find(o => o.id === id);
                          return order && order.status !== 'PAID_TO_MEDIATOR' && order.status !== 'PAID_TO_BUYER';
                        });
                      }

                      return (
                        <button 
                          key={status.value}
                          onClick={() => !isBulkDisabled && handleBulkMarkClick(status.value)}
                          disabled={isBulkDisabled}
                          style={{ 
                            width: '100%', 
                            padding: '8px 12px', 
                            border: 'none', 
                            background: 'none', 
                            textAlign: 'left', 
                            cursor: isBulkDisabled ? 'not-allowed' : 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            fontSize: '13px', 
                            borderRadius: '8px', 
                            color: isBulkDisabled ? '#cbd5e1' : '#475569',
                            opacity: isBulkDisabled ? 0.6 : 1
                          }}
                        >
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isBulkDisabled ? '#cbd5e1' : getStatusBadge(status.value).color }}></div>
                          {status.label}
                          {isBulkDisabled && <span style={{ marginLeft: 'auto', fontSize: '10px' }}>(Limited)</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <Button variant="secondary" onClick={() => fetchOrders(1, searchForm)} disabled={loading}>
              <Icon name="refresh" size={16} /> Refresh
            </Button>
          </div>
        </div>

        <CollapsibleFilterPanel
          title="Search & Filters"
          defaultOpen={false}
          activeCount={activeFilterCount}
          activeFilters={activeFilters}
          onReset={handleReset}
          onSearch={handleSearch}
          searchLoading={searchLoading}
        >
          <FilterField label="Order ID">
            <input
              type="text"
              placeholder="e.g. ORD-12345"
              value={searchForm.orderId}
              onChange={(e) => handleInputChange('orderId', e.target.value)}
              {...filterInputProps}
            />
          </FilterField>
          <FilterField label="Status">
            <SearchableSelect
              options={orderStatusOptions}
              value={searchForm.status}
              onChange={(value) => handleInputChange('status', value)}
              placeholder="All statuses"
              searchPlaceholder="Search status..."
            />
          </FilterField>
          <FilterField label="Buyer email">
            <input
              type="email"
              placeholder="buyer@email.com"
              value={searchForm.userEmail}
              onChange={(e) => handleInputChange('userEmail', e.target.value)}
              {...filterInputProps}
            />
          </FilterField>
          <FilterField label="Brand">
            <SearchableSelect
              options={brandOptions}
              value={searchForm.brandId}
              onChange={(value) => handleInputChange('brandId', value)}
              placeholder="All brands"
              searchPlaceholder="Search brand..."
            />
          </FilterField>
          <FilterField label="Product">
            <SearchableSelect
              options={productOptions}
              value={searchForm.productId}
              onChange={(value) => handleInputChange('productId', value)}
              placeholder="All products"
              searchPlaceholder="Search product..."
            />
          </FilterField>
        </CollapsibleFilterPanel>
      </div>
      <div className="orders-table-section" style={{ flex: 1, padding: '0 24px 24px', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc' }}>
        <div style={tableStyles.wrapper}>
          {loading ? (
            <div style={tableStyles.empty}>Loading orders...</div>
          ) : displayedOrders.length === 0 ? (
            <div style={tableStyles.empty}>No orders found</div>
          ) : (
            <>
              <div className="mobile-only-list">
                <div className="mobile-card-list" style={{ padding: '12px' }}>
                  {displayedOrders.map((order) => {
                    const badge = getStatusBadge(order.status);
                    const isSelected = selectedOrderIds.includes(order.id);
                    return (
                      <div key={order.id} className="mobile-card" style={{ borderColor: isSelected ? '#667eea' : undefined }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>{order.orderId}</div>
                            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{order.customer}</div>
                          </div>
                          <input type="checkbox" checked={isSelected} onChange={() => handleSelectOrder(order.id)} />
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Product</span>
                          <span className="mobile-card-value">{order.product}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Amount</span>
                          <span className="mobile-card-value">₹{order.amount}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Status</span>
                          <span style={statusBadge(badge.bg, badge.color)}>{badge.label}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Refund</span>
                          <RefundDeadlineBadge timeline={order.refundTimeline} />
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Refund Filled</span>
                          <span className="mobile-card-value">{order.refundFormFilledDate || '—'}</span>
                        </div>
                        <div className="mobile-card-actions">
                          <button type="button" onClick={() => handleViewOrder(order)} style={actionBtn('#3b82f6')}>
                            <Icon name="eye" size={14} /> View
                          </button>
                          <button type="button" onClick={() => handleDropdownToggle(order.id)} style={actionBtn(badge.bg, badge.color)}>
                            Status <Icon name="chev" size={12} />
                          </button>
                          <button type="button" onClick={() => handleWhatsAppClick(order.whatsappNumber)} style={actionBtn('#25D366')}>
                            <Icon name="wa" size={14} /> WhatsApp
                          </button>
                        </div>
                        {activeDropdown === order.id && (
                          <div style={{ marginTop: '8px', padding: '8px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            {[
                              { value: 'PAID_TO_MEDIATOR', label: 'Received Payment' },
                              { value: 'PAID_TO_BUYER', label: 'Paid to Buyer' },
                            ].map((status) => {
                              const isDisabled = status.value === 'PAID_TO_BUYER'
                                && order.status !== 'PAID_TO_MEDIATOR'
                                && order.status !== 'PAID_TO_BUYER';
                              return (
                                <button
                                  key={status.value}
                                  type="button"
                                  onClick={() => !isDisabled && handleUpdateStatus(order.id, status.value)}
                                  disabled={isDisabled}
                                  style={{ width: '100%', padding: '8px 10px', border: 'none', background: 'none', textAlign: 'left', cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: '13px', color: isDisabled ? '#cbd5e1' : '#475569' }}
                                >
                                  {status.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="desktop-only-table">
          <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
          <div className="table-scroll-container" style={tableStyles.scroll}>
            <table style={tableStyles.table}>
              <thead style={tableStyles.thead}>
                <tr>
                  <th style={{ ...tableStyles.th, width: '40px' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={displayedOrders.length > 0 && selectedOrderIds.length === displayedOrders.length}
                    />
                  </th>
                  <th style={tableStyles.th}>Order ID</th>
                  <th style={tableStyles.th}>Customer</th>
                  <th style={tableStyles.th}>Product</th>
                  <th style={tableStyles.th}>Amount</th>
                  <th style={tableStyles.th}>Status</th>
                  <th style={tableStyles.th}>Refund</th>
                  <th style={tableStyles.th}>Refund Filled</th>
                  <th style={{ ...tableStyles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={tableStyles.empty}>Loading orders...</td></tr>
                ) : displayedOrders.length === 0 ? (
                  <tr><td colSpan={9} style={tableStyles.empty}>No orders found</td></tr>
                ) : (
                  displayedOrders.map((order, index) => {
                    const badge = getStatusBadge(order.status);
                    const isSelected = selectedOrderIds.includes(order.id);
                    return (
                      <tr
                        key={order.id}
                        style={tableStyles.row(isSelected, index)}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#fafbfc'; }}
                      >
                        <td style={tableStyles.td}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleSelectOrder(order.id)} />
                        </td>
                        <td style={{ ...tableStyles.td, fontWeight: 600, color: '#1e293b' }}>{order.orderId}</td>
                        <td style={tableStyles.td}>
                          <div style={{ fontWeight: 500 }}>{order.customer}</div>
                          {order.userEmail && (
                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{order.userEmail}</div>
                          )}
                        </td>
                        <td style={{ ...tableStyles.td, maxWidth: '180px' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={order.product}>
                            {order.product}
                          </div>
                        </td>
                        <td style={{ ...tableStyles.td, fontWeight: 600 }}>₹{order.amount}</td>
                        <td style={tableStyles.td}>
                          <span style={statusBadge(badge.bg, badge.color)}>{badge.label}</span>
                        </td>
                        <td style={{ ...tableStyles.td, minWidth: '140px' }}>
                          <RefundDeadlineBadge timeline={order.refundTimeline} />
                        </td>
                        <td style={{ ...tableStyles.td, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {order.refundFormFilledDate || '—'}
                        </td>
                        <td style={{ ...tableStyles.td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => handleViewOrder(order)} style={actionBtn('#3b82f6')} title="View details">
                              <Icon name="eye" size={14} /> View
                            </button>
                            <div style={{ position: 'relative' }} className="dropdown-container">
                              <button
                                type="button"
                                onClick={() => handleDropdownToggle(order.id)}
                                disabled={updatingStatus === order.id}
                                style={{
                                  ...actionBtn(badge.bg, badge.color),
                                  border: `1px solid ${badge.color}30`,
                                }}
                                title="Change status"
                              >
                                Status <Icon name="chev" size={12} />
                              </button>
                              {activeDropdown === order.id && (
                                <div style={{
                                  position: 'absolute', right: 0, top: '100%',
                                  background: '#ffffff', border: '1px solid #e2e8f0',
                                  borderRadius: '10px', zIndex: 100,
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                  padding: '6px', minWidth: '180px', marginTop: '4px',
                                }}>
                                  {[
                                    { value: 'PAID_TO_MEDIATOR', label: 'Received Payment' },
                                    { value: 'PAID_TO_BUYER', label: 'Paid to Buyer' },
                                  ].map((status) => {
                                    const isDisabled = status.value === 'PAID_TO_BUYER'
                                      && order.status !== 'PAID_TO_MEDIATOR'
                                      && order.status !== 'PAID_TO_BUYER';
                                    return (
                                      <button
                                        key={status.value}
                                        type="button"
                                        onClick={() => !isDisabled && handleUpdateStatus(order.id, status.value)}
                                        disabled={isDisabled}
                                        style={{
                                          width: '100%', padding: '8px 10px', border: 'none',
                                          background: 'none', textAlign: 'left',
                                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                                          display: 'flex', alignItems: 'center', gap: '8px',
                                          fontSize: '12px', borderRadius: '6px',
                                          color: isDisabled ? '#cbd5e1' : '#475569',
                                        }}
                                      >
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isDisabled ? '#cbd5e1' : getStatusBadge(status.value).color }} />
                                        {status.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleWhatsAppClick(order.whatsappNumber)}
                              style={actionBtn('#25D366')}
                              title="WhatsApp buyer"
                            >
                              <Icon name="wa" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
              </div>
            </>
          )}
          <PaginationComponent pagination={pagination} onPageChange={fetchOrders} />
        </div>
      </div>

      {showOrderModal && selectedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Order Details</h3><p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Reference: {selectedOrder.orderId}</p></div>
              <button onClick={handleCloseModal} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer' }}><Icon name="x" size={20} /></button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                {/* Order Details Modal Content Same as Admin */}
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Basic Info</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Date: <strong>{selectedOrder.date}</strong></div>
                    <div>Amount: <strong>₹{selectedOrder.amount}</strong></div>
                    <div>Status: <strong style={{ color: getStatusBadge(selectedOrder.status).color }}>{getStatusBadge(selectedOrder.status).label}</strong></div>
                    <div>Refund Filled: <strong>{selectedOrder.refundFormFilledDate || 'MISSED / —'}</strong></div>
                    <div>Penalty: <strong style={{ color: Number(selectedOrder.penaltyAmount) > 0 ? '#dc2626' : '#059669' }}>{Number(selectedOrder.penaltyAmount) > 0 ? `₹${Number(selectedOrder.penaltyAmount).toLocaleString()}` : 'No Penalty'}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Customer</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Name: <strong>{selectedOrder.customer}</strong></div>
                    <div>Email: <strong>{selectedOrder.userEmail}</strong></div>
                    <div>WhatsApp: <strong>{selectedOrder.whatsappNumber}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Product</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Name: <strong>{selectedOrder.product}</strong></div>
                    <div>Brand: <strong>{selectedOrder.brandName || 'N/A'}</strong></div>
                  </div>
                </div>
                {buyerHasPaymentDetails(selectedOrder) && (
                  <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#166534', textTransform: 'uppercase' }}>Buyer Payment Details</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                      Submitted when the buyer filled the refund form.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                      <div>Method: <strong>{formatPaymentMethodLabel(selectedOrder.paymentMethod)}</strong></div>
                      {selectedOrder.accountHolderName && (
                        <div>Account holder: <strong>{selectedOrder.accountHolderName}</strong></div>
                      )}
                      {(selectedOrder.paymentMethod === 'UPI' || selectedOrder.upiId) && (
                        <div>UPI ID: <strong>{selectedOrder.upiId || '—'}</strong></div>
                      )}
                      {(selectedOrder.paymentMethod === 'ACCOUNT' || selectedOrder.bankAccount || selectedOrder.bankName) && (
                        <>
                          {selectedOrder.bankName && <div>Bank: <strong>{selectedOrder.bankName}</strong></div>}
                          {selectedOrder.bankAccount && <div>A/C No: <strong>{selectedOrder.bankAccount}</strong></div>}
                          {selectedOrder.ifscCode && <div>IFSC: <strong>{selectedOrder.ifscCode}</strong></div>}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {selectedOrder.reason && (
                  <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '12px', gridColumn: '1 / -1', border: '1px solid #fecaca' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#dc2626', textTransform: 'uppercase' }}>Reason</h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#dc2626', fontWeight: '500' }}>{selectedOrder.reason}</p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {showReasonModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '450px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>Reason for {getStatusBadge(pendingStatusUpdate?.targetStatus).label}</h3>
            <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>Please provide a reason for this status change. This will be visible to the buyer.</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason here..."
              style={{ width: '100%', minHeight: '120px', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', fontSize: '14px', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => { setShowReasonModal(false); setPendingStatusUpdate(null); }}>Cancel</Button>
              <Button onClick={confirmReasonUpdate}>Update Status</Button>
            </div>
          </div>
        </div>
      )}

      {showBulkConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '450px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>Confirm Bulk Status Change</h3>
            <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
              Are you sure you want to change the status of <strong>{selectedOrderIds.length}</strong> selected orders to <strong>{getStatusBadge(bulkTargetStatus).label}</strong>?
            </p>
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '24px', maxHeight: '150px', overflowY: 'auto' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Selected Order IDs:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {selectedOrderIds.map(id => (
                  <span key={id} style={{ fontSize: '12px', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', color: '#475569' }}>
                    {orders.find(o => o.id === id)?.orderId || id}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
              <Button onClick={confirmBulkMark} loading={isBulkMarking}>Confirm Bulk Update</Button>
            </div>
          </div>
        </div>
      )}

      {showStatusConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '400px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>Confirm Status Change</h3>
            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '14px' }}>Select the new status for this order:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
              {[
                { value: 'PAID_TO_MEDIATOR', label: 'Received Payment' },
                { value: 'PAID_TO_BUYER', label: 'Paid to Buyer' }
              ].map(status => {
                // Restriction: Can only change to PAID_TO_BUYER if current status is PAID_TO_MEDIATOR or PAID_TO_BUYER
                const isDisabled = status.value === 'PAID_TO_BUYER' && 
                                 statusToUpdate?.currentStatus !== 'PAID_TO_MEDIATOR' && 
                                 statusToUpdate?.currentStatus !== 'PAID_TO_BUYER';

                return (
                  <button 
                    key={status.value}
                    onClick={() => !isDisabled && confirmStatusUpdate(status.value)}
                    disabled={isDisabled}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0', 
                      background: isDisabled ? '#f8fafc' : '#fff', 
                      cursor: isDisabled ? 'not-allowed' : 'pointer', 
                      textAlign: 'left', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: isDisabled ? '#cbd5e1' : '#1e293b',
                      opacity: isDisabled ? 0.7 : 1
                    }}
                  >
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: isDisabled ? '#cbd5e1' : getStatusBadge(status.value).color }}></div>
                    {status.label}
                    {isDisabled && <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 'normal', color: '#94a3b8' }}>(Requires 'Paid to Mediator')</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowStatusConfirm(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediatorOrders;
